import { checkEmail } from './api';

/**
 * Looking the address up, once, for every door.
 *
 * Both the meaning and the trigger stay the door's own, and neither is an accident.
 *
 * A chapter or event visitor with an account is offered a login; the plans door
 * refuses the address, because discovering you already have an account is not what
 * buying a plan is for. RegistrationSpec::detectsAccount says which, and the caller
 * passes the handlers that follow.
 *
 * As for when: the modals ask on the first click of a two-stage button, because a
 * person has to type a password between the answer and the submit, and a click is
 * steadier than a blur under autofill, tabbing and password managers. The plans
 * door asks on blur, having no second stage. Pass `onBlur` to get the listener;
 * otherwise drive `check()` from wherever the door wants.
 *
 * Typing again clears whatever the last answer produced. Without that, somebody
 * can be shown the login state, edit the address and submit against a decision
 * made about a different person.
 *
 * `check()` hands back the lookup for the address in the field, still on its way or
 * already answered, so a submit can wait for it; nothing when there is nothing to
 * look up.
 */

const PATTERN = /^[\w-]+(?:\.[\w-]+)*@(?:[\w-]+\.)+[a-zA-Z]{2,7}$/;

export function watchEmail(form, handlers = {}) {
    const field = form.querySelector('#email') || form.querySelector('input[name="email"]');

    if (! field) {
        return { check: () => null };
    }

    const { onExisting, onNew, onReset, onError, onBlur = false } = handlers;

    let timer = null;
    let lastChecked = null;
    let lookup = null;

    function reset() {
        lastChecked = null;
        lookup = null;

        if (onReset) {
            onReset();
        }
    }

    function run() {
        const email = (field.value || '').trim();

        if (! email || ! PATTERN.test(email)) {
            reset();
            return null;
        }

        // The same address twice in a row is the same answer. Leaving and
        // re-entering a field is common and does not need a second round trip.
        if (email === lastChecked) {
            return lookup;
        }

        lastChecked = email;

        lookup = checkEmail(email)
            .done((data) => {
                if (data && data.exists) {
                    if (onExisting) onExisting(data, email);
                } else if (onNew) {
                    onNew(email);
                }
            })
            .fail(() => {
                lastChecked = null;
                lookup = null;

                if (onError) onError();
            });

        return lookup;
    }

    field.addEventListener('input', () => {
        clearTimeout(timer);
        reset();
    });

    if (onBlur) {
        field.addEventListener('blur', () => {
            clearTimeout(timer);
            timer = setTimeout(run, 150);
        });
    }

    // What a two-stage button calls on its first click. Also the way out when the
    // answer has not arrived yet, or the field was never left at all because the
    // address was filled in for them.
    return { check: run };
}
